import {
	Action,
	ActionTypes,
	IAction,
	IFilter,
	IMuteFilter,
	INotification,
	IPerformedAction,
	PerformedAction,
	PurgeConfig,
} from "../types";

import * as searchjs from "searchjs";
import { fastClone } from "../../../common/util";

const DEFAULT_TYPE_NAME = "default";

const KEY_NAME_DEFAULT_FIELDS = "defaults";
const KEY_NAME_DISMISS_BUTTON_TEXT = "defaultDismissButtonText";
const KEY_NAME_SHOW_DISMISS_ACTION = "showDismissAction";

const DISMISS_BUTTON_TEXT_FALLBACK = "Dismiss";

export default class ServiceHelper {
	/**
	 * Isolates the notification types from a specific part of the config tree
	 * @param config
	 */
	public static normaliseConfig(config: Record<string, object> | null): Record<string, object> {
		return {
			service: ServiceHelper.getServiceDefaults(config),
			types: ServiceHelper.getTypes(config),
		};
	}

	/**
	 * Gets the types from the config
	 *
	 * @param config
	 */
	public static getTypes(config: Record<string, object> | null): Record<string, object> {
		return Object.assign({}, config?.types ?? null);
	}

	/**
	 * Sets defaults for any missing config
	 *
	 * @param config
	 */
	public static getServiceDefaults(config: Record<string, object> | null): Record<string, object> {
		const defaultValues: PurgeConfig = {
			maxNotificationsToRetain: 1000,
			maxNotificationRetentionPeriodSeconds: false,
		};
		const defaults = Object.assign(defaultValues, config);
		if (defaults.hasOwnProperty("types")) {
			delete defaults["types"];
		}

		if (defaults.hasOwnProperty("presentationComponents")) {
			delete defaults["presentationComponents"];
		}

		return defaults;
	}

	/**
	 * Applies the defaults defined in the config object to a notification base on the INotification.type
	 *
	 * @param config {Object}
	 * @param notification {INotification}
	 */
	public static applyDefaults(config: any, notification: INotification): INotification {
		let configToApply;
		const notificationType = notification.type;

		if (notificationType && config?.types?.[notificationType]) {
			configToApply = config.types[notificationType];
		} else if (config?.types?.[DEFAULT_TYPE_NAME]) {
			configToApply = config.types[DEFAULT_TYPE_NAME];
		}

		if (!Array.isArray(notification.actions)) {
			notification.actions = [];
		}

		if (!Array.isArray(notification.actionsHistory)) {
			notification.actionsHistory = [];
		}

		if (!Array.isArray(notification.stateHistory)) {
			notification.stateHistory = [];
		}

		// A config has not been supplied at all
		if (configToApply) {
			let returnValue = fastClone(notification) as INotification;

			if (configToApply.hasOwnProperty(KEY_NAME_DEFAULT_FIELDS)) {
				returnValue = ServiceHelper.setDefaultsFromConfig(
					notification,
					configToApply[KEY_NAME_DEFAULT_FIELDS]
				) as INotification;
			}

			const showDismissAction = configToApply.hasOwnProperty(KEY_NAME_SHOW_DISMISS_ACTION)
				? configToApply[KEY_NAME_SHOW_DISMISS_ACTION]
				: false;

			if (showDismissAction) {
				let dismissText = DISMISS_BUTTON_TEXT_FALLBACK;

				if (configToApply.hasOwnProperty(KEY_NAME_DISMISS_BUTTON_TEXT)) {
					dismissText = configToApply[KEY_NAME_DISMISS_BUTTON_TEXT];
				} else if (config.hasOwnProperty("service") && config.service.hasOwnProperty(KEY_NAME_DISMISS_BUTTON_TEXT)) {
					dismissText = config.service[KEY_NAME_DISMISS_BUTTON_TEXT];
				}
				returnValue = ServiceHelper.addDismissActionToNotification(returnValue, dismissText);
			}

			return returnValue;
		} else {
			return notification;
		}
	}

	/**
	 * Adds a dismiss action to a notification if one does not already exist
	 * @param notification
	 * @param buttonText
	 */
	public static addDismissActionToNotification(notification: INotification, buttonText: string): INotification {
		if (!ServiceHelper.hasDismissAction(notification)) {
			const action = new Action();
			action.type = ActionTypes.DISMISS;
			action.buttonText = buttonText;
			notification.actions?.push(action);
		}

		return notification;
	}

	public static hasDismissAction(notification: INotification) {
		let returnValue = false;
		notification.actions
			?.reduce((accumulator: any, value) => accumulator.concat(value), [])
			.forEach((action) => {
				if (action.type.toLowerCase() === "dismiss") {
					returnValue = true;
				}
			});

		return returnValue;
	}

	/**
	 * Recursively sets the value if it's not already set
	 *
	 * @param notification
	 * @param config
	 */
	public static setDefaultsFromConfig(notification: object, config: object) {
		for (const [key, value] of Object.entries(config)) {
			const valueOnNotification = notification[key];
			if (
				typeof valueOnNotification === "undefined" ||
				valueOnNotification === "" ||
				(Array.isArray(valueOnNotification) && valueOnNotification.length === 0)
			) {
				notification[key] = value;
			} else if (valueOnNotification && typeof valueOnNotification === "object" && typeof value === "object") {
				notification[key] = ServiceHelper.setDefaultsFromConfig(valueOnNotification, value);
			}
		}
		return notification;
	}

	public static filterMatches(filter: IFilter | undefined | null, notification: INotification): boolean {
		// All notifications match if the filters are empty

		const includeExists: boolean = (filter?.include?.length ?? 0) > 0;
		const excludeExists: boolean = (filter?.exclude?.length ?? 0) > 0;

		if (!includeExists && !excludeExists) {
			// Empty filters will match everything
			return true;
		}

		let isMatch = !includeExists;

		if (includeExists) {
			filter?.include?.forEach((filterToMatch) => {
				if (searchjs.matchObject(notification, filterToMatch)) {
					isMatch = true;
				}
			});
		}

		if (excludeExists) {
			filter?.exclude?.forEach((filterToMatch) => {
				if (searchjs.matchObject(notification, filterToMatch)) {
					isMatch = false;
				}
			});
		}

		return isMatch;
	}

	/**
	 * Converts an IAction into and IPerformedAction and places it the actionsHistory
	 *
	 * @param notification {INotification}
	 * @param action {IAction}
	 * @return INotification
	 *
	 * Note: JavaScript is pass by reference for objects but prefer to be specific by returning a value
	 * not sure if putting a return in is confusing and hinting at it being pass by reference.
	 */
	public static addPerformedAction(notification: INotification, action: IAction): INotification {
		let clone = fastClone(notification) as INotification;

		const performedAction = new PerformedAction();
		performedAction.id = action.id || "";
		performedAction.type = action.type;
		performedAction.datePerformed = new Date().toISOString();

		const actionsHistory: IPerformedAction[] = (clone.actionsHistory ?? []).slice(0);
		actionsHistory.push(performedAction);
		clone.actionsHistory = actionsHistory;

		return clone;
	}

	/**
	 * Applies the history states to a notification which if finds in a lists
	 *
	 * @param notification
	 * @param notificationList
	 * @param defaultPrevious
	 */
	public static setNotificationHistory(
		notification: INotification,
		notificationList: Map<string, INotification>,
		defaultPrevious: INotification
	): INotification {
		const clone = fastClone(notification) as INotification;

		let currentlyStoredNotification: INotification | undefined;
		let currentHistory: INotification[];

		if (notification.id && notificationList.has(notification.id)) {
			currentlyStoredNotification = notificationList.get(notification.id);
			currentHistory = currentlyStoredNotification?.stateHistory ?? [];
		} else {
			currentHistory = clone.stateHistory;
			currentlyStoredNotification = defaultPrevious;
		}

		if (currentlyStoredNotification) {
			currentlyStoredNotification.stateHistory = [];
			currentHistory.push(currentlyStoredNotification);
		}

		clone.stateHistory = currentHistory;
		return clone;
	}

	/**
	 * Get a list of notifications that qualify the for the  purge from storage requirements
	 *
	 * @param notifications
	 * @param purgeConfig
	 */
	public static getItemsToPurge(notifications: Map<string, INotification>, purgeConfig: PurgeConfig): INotification[] {
		const items: INotification[] = [];

		// If it's not set we have all collected all the old ones already
		let gotAllExpired = purgeConfig.maxNotificationRetentionPeriodSeconds === false;
		let count = 0;
		const iterable = notifications.entries();

		do {
			const next = iterable.next();
			if (next.done) {
				break;
			}
			if (purgeConfig.maxNotificationRetentionPeriodSeconds) {
				const { actionsHistory } = next.value[1];
				if (actionsHistory && actionsHistory.length) {
					const lastUpdatedDate = Date.parse(actionsHistory[actionsHistory.length - 1].datePerformed);

					if (Date.now() - purgeConfig.maxNotificationRetentionPeriodSeconds * 1000 > lastUpdatedDate) {
						count++;
						items.push(next.value[1]);
						continue;
					} else {
						/**
						 * Notifications are stored is oldest first in the data structure
						 * if the statement does not match we can stop all checks
						 */
						gotAllExpired = true;
					}
				}
			}

			if (notifications.size - count > purgeConfig.maxNotificationsToRetain) {
				count++;
				items.push(next.value[1]);
			}
		} while (!gotAllExpired || notifications.size - count > purgeConfig.maxNotificationsToRetain);

		return items;
	}

	public static matchesMuteFilters(notification: INotification, muteFilters: IMuteFilter[]): boolean {
		let isInArray = false;
		muteFilters.forEach((muteFilter: IMuteFilter) => {
			// if filter has both type and source - must match both
			if (!isInArray && muteFilter.source && muteFilter.type) {
				isInArray = muteFilter.source === notification.source && muteFilter.type === notification.type;
			} else if ((!isInArray && muteFilter.source === notification.source) || muteFilter.type === notification.type) {
				isInArray = true;
			}
		});

		return isInArray;
	}
}
